import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { LockClosedIcon } from '@radix-ui/react-icons';
import { ProTag, Switch } from '@producer/ui-kit';

import { SettingsSection } from './settings-section';
import { SettingsAction } from '../../user/components/settings-action';
import { toastPool } from '../../toasts/models/toast-pool';
import { Toast } from '../../toasts/models/toast';
import { catchError } from '../../../core/catch-error';

import type { Space } from '../../../app/entities/space';

type SharePasswordPolicyProps = {
  space: Space;
};

/**
 * Workspace switch that makes a password mandatory on new share links. Saved through
 * `PATCH /spaces/:id`, which only owners and admins may call. A Pro feature: free workspaces see
 * it disabled, with a Pro tag.
 */
export const SharePasswordPolicy: React.FC<SharePasswordPolicyProps> = observer(({ space }) => {
  const [isSaving, setIsSaving] = useState(false);
  const isProLocked = !space.hasSubscription();

  const handleToggle = async (requireSharePassword: boolean) => {
    setIsSaving(true);
    try {
      await space.update({ security: { ...space.security, requireSharePassword } });
      toastPool.insert(
        new Toast(
          requireSharePassword
            ? 'Share links now require a password.'
            : 'Share links no longer require a password.',
        ),
      );
    } catch (e) {
      catchError(e);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <SettingsSection
      title="Share links"
      description="New share links can't be created without a password. Existing links keep working until you edit them."
    >
      <SettingsAction
        title="Require a password on all share links"
        icon={<LockClosedIcon />}
        tag={isProLocked && <ProTag />}
        action={
          <Switch
            isToggled={space.requiresSharePassword}
            onToggle={handleToggle}
            isDisabled={isSaving || isProLocked}
          />
        }
      />
    </SettingsSection>
  );
});
